import { mapActions } from 'vuex';

import { api } from '_/services/api';

// Daily-glance dashboard: numbers come from cached external APIs (GA4 +
// stores) and cheap local queries, so a slow 5-min poll is plenty. The
// stakeholder reads this once or twice a day; the Refresh button + range
// change pull on demand.
const POLL_INTERVAL_MS = 300000;

const RANGE_PRESETS = [
    { key: 'today', label: 'Today' },
    { key: '7d',    label: '7 days' },
    { key: '28d',   label: '28 days' },
    { key: 'wc',    label: 'World Cup' },
    { key: 'custom', label: 'Custom' },
];

function formatNumber (n) {
    if (n === null || n === undefined) return '—';
    if (typeof n !== 'number') return String(n);
    return n.toLocaleString('en-US');
}

function formatPercent (frac, digits = 1) {
    if (frac === null || frac === undefined) return '—';
    return (frac * 100).toFixed(digits) + '%';
}

function formatDuration (seconds) {
    if (seconds === null || seconds === undefined) return '—';
    const s = Math.round(seconds);
    if (s < 60) return s + 's';
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return m + 'm ' + rem + 's';
    const h = Math.floor(m / 60);
    return h + 'h ' + (m % 60) + 'm';
}

function formatMoney (amount, currency) {
    if (amount === null || amount === undefined) return '—';
    return (currency || 'EUR') + ' ' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatRelative (iso) {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const dt = Date.now() - t;
    if (dt < 60_000)     return Math.floor(dt / 1000) + 's ago';
    if (dt < 3_600_000)  return Math.floor(dt / 60_000) + 'm ago';
    if (dt < 86_400_000) return Math.floor(dt / 3_600_000) + 'h ago';
    return Math.floor(dt / 86_400_000) + 'd ago';
}

export default {
    name: 'KpiMonitor',

    data () {
        return {
            kpi: null,
            snapshotAt: null,
            loading: false,
            error: null,
            range: '28d',
            customStart: '',
            customEnd: '',
            presets: RANGE_PRESETS,
            // inline manual-input editor
            editing: false,
            inputAdSpend: '',
            inputContentCost: '',
            inputCurrency: 'EUR',
            saving: false,
            _pollHandle: null,
        };
    },

    computed: {
        breadcrumbItems () {
            return [{ text: 'WC KPIs', link: 'wc-kpis' }];
        },

        platformRows () {
            if (!this.kpi || !this.kpi.ga4) return [];
            const order = ['iOS', 'Android', 'web'];
            const bp = this.kpi.ga4.by_platform || {};
            return order
                .filter(p => bp[p])
                .map(p => Object.assign({ platform: p === 'web' ? 'Web' : p }, bp[p]));
        },

        // The month the manual editor writes to — anchored to the range end.
        editPeriod () {
            const end = this.kpi && this.kpi.range ? this.kpi.range.end : null;
            if (!end) return '';
            return end.slice(0, 7); // YYYY-MM
        },
    },

    methods: {
        ...mapActions('ui', [
            'animateWrapper',
            'setBodyClass',
            'setBreadcrumbItems',
        ]),

        formatNumber,
        formatPercent,
        formatDuration,
        formatMoney,
        formatRelative,

        rangeQuery () {
            if (this.range === 'custom' && this.customStart && this.customEnd) {
                return 'range=custom&start=' + this.customStart + '&end=' + this.customEnd;
            }
            return 'range=' + this.range;
        },

        selectRange (key) {
            this.range = key;
            if (key !== 'custom') {
                this.refresh();
            }
        },

        applyCustom () {
            if (this.customStart && this.customEnd) {
                this.refresh();
            }
        },

        async refresh (opts) {
            this.loading = true;
            const bust = opts && opts.force ? '&refresh=1' : '';
            try {
                const response = await api.get('admin/kpis/summary?' + this.rangeQuery() + bust);
                // PCMS double-wraps standard controller responses.
                const outer = response && response.body ? response.body : response;
                const payload = outer && outer.body ? outer.body : outer;
                this.kpi = payload;
                this.snapshotAt = payload.as_of;
                this.error = null;
                this.seedEditor();
            } catch (exc) {
                this.error = (exc && exc.message)
                    || (exc && exc.response && exc.response.data && exc.response.data.message)
                    || 'Could not load KPI metrics.';
            } finally {
                this.loading = false;
            }
        },

        seedEditor () {
            const m = this.kpi && this.kpi.manual ? this.kpi.manual : {};
            this.inputAdSpend = m.ad_spend !== null && m.ad_spend !== undefined ? m.ad_spend : '';
            this.inputContentCost = m.content_cost !== null && m.content_cost !== undefined ? m.content_cost : '';
            this.inputCurrency = m.currency || 'EUR';
        },

        async saveInputs () {
            if (!this.editPeriod) return;
            this.saving = true;
            try {
                const calls = [];
                if (this.inputAdSpend !== '' && this.inputAdSpend !== null) {
                    calls.push(api.post('admin/kpis/inputs', {
                        metric: 'ad_spend', period: this.editPeriod,
                        amount: Number(this.inputAdSpend), currency: this.inputCurrency,
                    }));
                }
                if (this.inputContentCost !== '' && this.inputContentCost !== null) {
                    calls.push(api.post('admin/kpis/inputs', {
                        metric: 'content_cost', period: this.editPeriod,
                        amount: Number(this.inputContentCost), currency: this.inputCurrency,
                    }));
                }
                await Promise.all(calls);
                this.editing = false;
                await this.refresh();
            } catch (exc) {
                this.error = 'Could not save inputs.';
            } finally {
                this.saving = false;
            }
        },

        startPolling () {
            this.refresh();
            this._pollHandle = setInterval(() => this.refresh(), POLL_INTERVAL_MS);
        },

        stopPolling () {
            if (this._pollHandle) {
                clearInterval(this._pollHandle);
                this._pollHandle = null;
            }
        },
    },

    mounted () {
        this.$nextTick(function () {
            this.setBodyClass('admin');
            this.setBreadcrumbItems(this.breadcrumbItems);
            this.animateWrapper();
            this.startPolling();
        });
    },

    beforeDestroy () {
        this.stopPolling();
    },
};
